
import React, { Component } from 'react';
import type { Dispatch } from 'redux';

import { isMobileBrowser } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import { Icon, IconArrowDown, IconArrowUp, IconWarning } from '../../../base/icons';
import { setAudioMuted } from '../../../base/media';
import { getLocalParticipant } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { ToolboxButtonWithIcon } from '../../../base/toolbox/components';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { getLocalMHAudioTrack } from '../../../base/tracks';
import { AudioSettingsPopup, toggleAudioSettings } from '../../../settings';
import { isAudioSettingsButtonDisabled } from '../../functions';
import { requestMediaPermissions, showPermissionGuidance } from '../../utils/permissionUtils';
import AudioMuteButton from '../AudioMuteButton';

type Props = {

  /**
   * Indicates whether audio permissions have been granted or denied.
   */
  hasPermissions: boolean,

  /**
   * Whether there is an audio track or not.
   */
  hasAudioTrack: boolean,

  /**
   * Click handler for the small icon. Opens audio options.
   */
  onAudioOptionsClick: Function,

  /**
   * If the button should be disabled.
   */
  isDisabled: boolean,

  /**
   * Used for translation.
   */
  t: Function,

  /**
   * Flag controlling the visibility of the button.
   * AudioSettings popup is disabled on mobile browsers.
   */
  visible: boolean,

    /**
   * The redux {@code dispatch} function.
   */
  dispatch: Dispatch<any>,
};

/**
 * Button used for audio & audio settings.
 *
 * @returns {ReactElement}
 */
class AudioSettingsButton extends Component<Props> {
    state = {
        permissionRequestInProgress: false
    };

    /**
     * Returns true if the settings icon is disabled.
     *
     * @returns {boolean}
     */
    _isIconDisabled() {
        const { hasPermissions, hasAudioTrack, isDisabled } = this.props;

        return (!hasPermissions && !hasAudioTrack) || Boolean(isDisabled);
    }

    /**
       * Calls into legacy UI to update the application layout, if necessary.
       *
       * @inheritdoc
       * returns {void}
       */
    componentDidUpdate(prevProps) {
        const { hasPermissions, isMuted, dispatch } = this.props;

        if (prevProps.hasPermissions === false && hasPermissions === true && isMuted === true) {
            // If permissions were just granted and audio is muted, unmute it.
            dispatch(setAudioMuted(false));
        }
    }

    /**
   * Implements React's {@link Component#render}.
   *
   * @inheritdoc
   */
    render() {
        const {
            hasAudioTrack,
            onAudioOptionsClick,
            hidepopup,
            t,
            _isAudioLocked,
            _audioSettingsVisible
        } = this.props;

        const isIconDisabled = this._isIconDisabled();
        const showWarning = isIconDisabled && !hasAudioTrack;

        const tooltip = _isAudioLocked
            ? t('toolbar.audioLocked')
            : isIconDisabled
                ? t('toolbar.audioUnavailable')
                : t('toolbar.audioSettings');

        return showWarning ? (
            <Tooltip
                content = { tooltip }
                position = { 'top' }>
                <div
                    className = 'IconWarningButtonParent'
                    onClick = { this.state.permissionRequestInProgress ? undefined : this._onPermissionRequest }>
                    <div className = 'IconWarningButton'>
                        <Icon src = { IconWarning } />
                    </div>
                    <AudioMuteButton />
                </div>
            </Tooltip>
        ) : isMobileBrowser() || hidepopup ? (
            <Tooltip
                content = { tooltip }
                position = { 'top' }>
                <div className = 'settings-button-child'>
                    <AudioMuteButton />
                </div>
            </Tooltip>
        ) : (
            <AudioSettingsPopup>
                <Tooltip
                    content = { tooltip }
                    position = { 'top' }>
                    <ToolboxButtonWithIcon
                        icon = { _audioSettingsVisible ? IconArrowDown : IconArrowUp }
                        iconDisabled = { isIconDisabled }
                        onIconClick = { onAudioOptionsClick }>
                        <AudioMuteButton />
                    </ToolboxButtonWithIcon>
                </Tooltip>
            </AudioSettingsPopup>
        );
    }

    /**
     * Handles permission request click.
     *
     * @private
     * @returns {void}
     */
    _onPermissionRequest = async () => {
        this.setState({ permissionRequestInProgress: true });

        try {
            // video must be requested explicitly: it defaults to true, so asking for the microphone also asked for
            // the camera and a missing/denied camera failed the whole request - the mic permission this button
            // exists for was never granted.
            const result = await requestMediaPermissions({ audio: true,
                video: false });

            if (result.showGuidance) {
                // Show the permission guidance dialog with browser-specific instructions
                showPermissionGuidance([ 'audio' ]);
            }
        } finally {
            this.setState({ permissionRequestInProgress: false });
        }
    };
}

/**
 * Function that maps parts of Redux state tree into component props.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
    const { permissions = {} } = state['features/base/devices'];
    const { audioSettingsVisible } = state['features/settings'];
    const localParticipantID = getLocalParticipant(state)?.id;
    const { lockAudio } = state['features/base/conference'];
    const isMuted = state['features/base/media'].audio.muted;

    return {
        hasPermissions: permissions.audio,
        hasAudioTrack: Boolean(getLocalMHAudioTrack(state)),
        isDisabled: isAudioSettingsButtonDisabled(state),
        visible: !isMobileBrowser(),
        isMuted,
        _isAudioLocked: lockAudio[`${localParticipantID}`],
        _audioSettingsVisible: audioSettingsVisible
    };
}

const mapDispatchToProps = {
    onAudioOptionsClick: toggleAudioSettings
};

export default translate(
  connect(mapStateToProps, mapDispatchToProps)(AudioSettingsButton)
);
